/**
 * Shared variation formatting utilities for consistent display across pages and emails
 * Extracts variation details (species, morph, life stage, gender) for display
 */

import React from 'react';
import { formatSpeciesName } from './scientificName';

/**
 * Format variation details into display lines
 * @param {Object} variation - Product variation object with morph, product, variation_type relations
 * @returns {Array<string>} Array of formatted lines for display
 */
export const formatVariationLines = (variation) => {
  if (!variation) return [];
  
  const v = variation;
  const product = v?.product;
  const speciesObj = product?.species_id; // This should be the full Species object if properly included
  const morph = v?.morph;
  
  const lines = [];
  
  // Species info (genus + species + locality)
  if (speciesObj?.genus && speciesObj?.species) {
    lines.push(formatSpeciesName(speciesObj));
  }
  
  // Morph info
  if (morph?.name) {
    lines.push(`${morph.name} morph`);
  }
  
  // Use variation_type.name directly as the canonical label
  // e.g., "Adult Male", "Adult Female", "Unsexed Adult", "Juvenile", "Subadult"
  const variationType = v?.variation_type?.name;
  if (variationType) {
    lines.push(variationType);
  }
  
  return lines;
};

/**
 * Render variation lines as HTML for React components
 * @param {Object} variation - Product variation object
 * @param {string} className - CSS class for styling (default: "small text-muted")
 * @returns {JSX.Element|null} Formatted variation details or null
 */
export const renderVariationLines = (variation, className = "small text-muted") => {
  const lines = formatVariationLines(variation);
  
  return lines.length > 0 ? (
    <div className="mb-2">
      {lines.map((line, idx) => (
        <div key={idx} className={className}>• {line}</div>
      ))}
    </div>
  ) : null;
};

/**
 * Format variation lines as plain text for emails
 * @param {Object} variation - Product variation object
 * @returns {string} Formatted variation details as plain text
 */
export const formatVariationLinesText = (variation) => {
  const lines = formatVariationLines(variation);
  return lines.map(line => `• ${line}`).join('\n');
};